import { useMemo } from 'react'

import {
  ArrayFieldModel,
  CheckboxFieldModel,
  DateFieldModel,
  FieldType,
  FileFieldModel,
  FixedFileFieldModel,
  FixedPictureFieldModel,
  FixedTextFieldModel,
  FormField,
  FormModelStructure,
  PictureFieldModel,
  RadioFieldModel,
  SectionFieldModel,
  SelectEditableFieldModel,
  SelectFieldModel,
  SignatureFieldModel,
  TextFieldModel,
  TitleFieldModel,
} from '@types'

import { SubmitterFieldArray } from '../FormSubmitter/components/SubmitterFieldArray'
import { SubmitterFieldCheckbox } from '../FormSubmitter/components/SubmitterFieldCheckbox'
import { SubmitterFieldDate } from '../FormSubmitter/components/SubmitterFieldDate'
import { SubmitterFieldFile } from '../FormSubmitter/components/SubmitterFieldFile'
import { SubmitterFieldFixedFile } from '../FormSubmitter/components/SubmitterFieldFixedFile'
import { SubmitterFieldFixedPicture } from '../FormSubmitter/components/SubmitterFieldFixedPicture'
import { SubmitterFieldFixedText } from '../FormSubmitter/components/SubmitterFieldFixedText'
import { SubmitterFieldPicture } from '../FormSubmitter/components/SubmitterFieldPicture'
import { SubmitterFieldRadio } from '../FormSubmitter/components/SubmitterFieldRadio'
import { SubmitterFieldSection } from '../FormSubmitter/components/SubmitterFieldSection'
import { SubmitterFieldSelect } from '../FormSubmitter/components/SubmitterFieldSelect'
import { SubmitterFieldSelectEditable } from '../FormSubmitter/components/SubmitterFieldSelectEditable'
import { SubmitterFieldSignature } from '../FormSubmitter/components/SubmitterFieldSignature'
import { SubmitterFieldText } from '../FormSubmitter/components/SubmitterFieldText'
import { SubmitterFieldTitle } from '../FormSubmitter/components/SubmitterFieldTitle'

import { FakeContainer, Field, FormContainer } from './FormSubmitterPreview.styled'

type FormSubmitterPreviewProps = {
  formModel: FormModelStructure
}

export const FormSubmitterPreview: React.FC<FormSubmitterPreviewProps> = ({ formModel }: FormSubmitterPreviewProps) => {
  const fields = formModel.fields

  const renderField = (field: FormField) => {
    const commonProps = {
      onChange: () => {},
      field: field,
      disabled: true,
    }

    const optionsProps = {
      ...commonProps,
      onRedirect: () => {},
    }

    const arrayProps = {
      ...commonProps,
      scrollTo: () => {},
    }

    switch (field.type) {
      case FieldType.Title:
        return <SubmitterFieldTitle field={field as TitleFieldModel} />
      case FieldType.Section:
        return <SubmitterFieldSection field={field as SectionFieldModel} />
      case FieldType.Array:
        return <SubmitterFieldArray {...arrayProps} field={field as ArrayFieldModel} isPreview />
      case FieldType.Text:
        return <SubmitterFieldText {...commonProps} field={field as TextFieldModel} />
      case FieldType.Date:
        return <SubmitterFieldDate {...commonProps} field={field as DateFieldModel} />
      case FieldType.Select:
        return <SubmitterFieldSelect {...optionsProps} field={field as SelectFieldModel} />
      case FieldType.SelectEditable:
        return <SubmitterFieldSelectEditable {...optionsProps} field={field as SelectEditableFieldModel} />
      case FieldType.Signature:
        return <SubmitterFieldSignature {...commonProps} field={field as SignatureFieldModel} />
      case FieldType.Radio:
        return <SubmitterFieldRadio {...optionsProps} field={field as RadioFieldModel} />
      case FieldType.Checkbox:
        return <SubmitterFieldCheckbox {...commonProps} field={field as CheckboxFieldModel} />
      case FieldType.File:
        return <SubmitterFieldFile {...commonProps} field={field as FileFieldModel} />
      case FieldType.Picture:
        return <SubmitterFieldPicture {...commonProps} field={field as PictureFieldModel} />
      case FieldType.FixedText:
        return <SubmitterFieldFixedText field={field as FixedTextFieldModel} />
      case FieldType.FixedFile:
        return <SubmitterFieldFixedFile field={field as FixedFileFieldModel} />
      case FieldType.FixedPicture:
        return <SubmitterFieldFixedPicture field={field as FixedPictureFieldModel} />
      default:
        return null
    }
  }

  const filteredFields = useMemo(() => {
    return [...fields.filter((field) => field.type !== FieldType.Title && field.type !== FieldType.HeaderPicture)]
  }, [fields])

  const [firstField, secondField] = filteredFields

  if (!firstField) return null

  return (
    <FormContainer>
      <FakeContainer />
      <Field>
        {renderField(firstField)}
        {firstField.type == FieldType.Section && secondField && renderField(secondField)}
      </Field>
    </FormContainer>
  )
}
